/* eslint-disable react-refresh/only-export-components */
/* eslint-disable react-hooks-extra/no-direct-set-state-in-use-effect */
import type { CheckboxProps } from '@connected/ui/components/checkbox';
import type { HTMLAttributes, ReactElement, ReactNode } from 'react';

import { Badge } from '@connected/ui/components/badge';
import { Button } from '@connected/ui/components/button';
import { Checkbox } from '@connected/ui/components/checkbox';
import { Label } from '@connected/ui/components/label';
import { cn } from '@connected/ui/lib/utils';
import { Slot } from '@radix-ui/react-slot';
import { ChevronDown, X } from 'lucide-react';
import { createContext, use, useCallback, useEffect, useMemo, useState } from 'react';

import type { OptionFieldContextState } from '../../hooks/use-option-field';
import type { Option, OptionExtra } from '../../lib/option';
import type { PrimativeFieldProps, PrimativeFormPropsOmit, PrimitiveValueType } from '../../lib/utils';

import { OptionFieldContext, useOptionField } from '../../hooks/use-option-field';
import { createValueMapsFromOptions } from '../../lib/option';
import { valueToString } from '../../lib/utils';

export type CheckboxGroupOption<Value extends PrimitiveValueType, Extra extends OptionExtra | undefined = undefined> = Option<Value, Extra>;
export type CheckboxGroupRenderItem<Value extends PrimitiveValueType, Extra extends OptionExtra | undefined = undefined> = (props: CheckboxGroupItemProps<Value, Extra>) => ReactNode;
export type AnyCheckboxGroupRenderItem = CheckboxGroupRenderItem<any, any>;

export type CheckboxGroupContextState<Value extends PrimitiveValueType, Extra extends OptionExtra | undefined = undefined> = {
    name?: string;
    value: Value[] | undefined;
    onChange: (value: Value[]) => void;
    onBlur?: () => void;
    disabled?: boolean;
    selectedValues: Value[];
    handleCheckedChange: (checked: boolean, option: Option<Value, Extra>) => void;
    isOptionChecked: (option: Option<Value, Extra>) => boolean;
    valueToString: Map<Value, string>;
    RenderItem: CheckboxGroupRenderItem<Value, Extra>;
};

export const CheckboxGroupContext = createContext<CheckboxGroupContextState<PrimitiveValueType, OptionExtra | undefined> | null>(null);

export function useCheckboxGroupContext<Value extends PrimitiveValueType, Extra extends OptionExtra | undefined = undefined>() {
    const context = use(CheckboxGroupContext);

    if (!context)
        throw new Error('useCheckboxGroupContext must be used within a CheckboxGroup');

    return context as unknown as CheckboxGroupContextState<Value, Extra>;
}

export type CheckboxGroupItemProps<Value extends PrimitiveValueType, Extra extends OptionExtra | undefined = undefined> = Omit<CheckboxProps, 'onCheckedChange'> & {
    option: CheckboxGroupOption<Value, Extra>;
    name?: string;
    stringValue: string;
    disabled?: boolean;
    isChecked: boolean;
    onCheckedChange: (checked: boolean, option: Option<Value, Extra>) => void;
};

export function DefaultCheckboxGroupItem<Value extends PrimitiveValueType, Extra extends OptionExtra | undefined = undefined>({
    name,
    option,
    stringValue,
    disabled,
    isChecked,
    onCheckedChange,
    ...props
}: CheckboxGroupItemProps<Value, Extra>) {
    const id = name ? `${name}-${stringValue}` : stringValue;

    return (
        <Label id={id} disabled={disabled} className="flex items-center gap-2 cursor-pointer">
            <Checkbox
                id={id}
                checked={isChecked}
                onCheckedChange={checked => onCheckedChange(!!checked, option)}
                disabled={disabled}
                {...props}
            />
            <span className="text-sm">{option.label}</span>
        </Label>
    );
}

DefaultCheckboxGroupItem.displayName = 'DefaultCheckboxGroupItem';

export function CheckboxGroupItems<Value extends PrimitiveValueType, Extra extends OptionExtra | undefined = undefined>() {
    const { options } = useOptionField<CheckboxGroupOption<Value, Extra>>();
    const {
        name,
        handleCheckedChange,
        isOptionChecked,
        valueToString,
        RenderItem,
        disabled,
        onBlur,
    } = useCheckboxGroupContext<Value, Extra>();

    return (
        <>
            {options.map((option) => {
                const stringValue = valueToString.get(option.value) || String(option.value);
                const id = name ? `${name}-${stringValue}` : stringValue;

                function itemOnCheckedChange(checked: boolean, opt: Option<Value, Extra>) {
                    handleCheckedChange(checked, opt);
                }

                return (
                    <RenderItem
                        key={id}
                        id={id}
                        name={name}
                        option={option}
                        stringValue={stringValue}
                        isChecked={isOptionChecked(option)}
                        onCheckedChange={itemOnCheckedChange}
                        disabled={disabled || option.disabled}
                        onBlur={onBlur}
                    />
                );
            })}
        </>
    );
}

CheckboxGroupItems.displayName = 'CheckboxGroupItems';

export type CheckboxGroupProps<Value extends PrimitiveValueType, Extra extends OptionExtra | undefined = undefined, RenderItem extends CheckboxGroupRenderItem<Value, Extra> = typeof DefaultCheckboxGroupItem>
    = | (PrimativeFieldProps<Value[]> & {
        asChild: true;
        options: CheckboxGroupOption<Value, Extra>[];
        orientation?: 'horizontal' | 'vertical';
        renderItem?: RenderItem;
        className?: string;
        children: ReactNode;
    })
    | (PrimativeFieldProps<Value[]> & {
        asChild?: false;
        options: CheckboxGroupOption<Value, Extra>[];
        orientation?: 'horizontal' | 'vertical';
        renderItem?: RenderItem;
        className?: string;
        children?: ReactNode;
    } & Omit<HTMLAttributes<HTMLDivElement>, PrimativeFormPropsOmit>);

export function CheckboxGroup<Value extends PrimitiveValueType, Extra extends OptionExtra | undefined = undefined, RenderItem extends CheckboxGroupRenderItem<Value, Extra> = typeof DefaultCheckboxGroupItem>(
    props: PrimativeFieldProps<Value[]> & {
        asChild: true;
        options: CheckboxGroupOption<Value, Extra>[];
        orientation?: 'horizontal' | 'vertical';
        renderItem?: RenderItem;
        className?: string;
        children: ReactNode;
    },
): ReactElement;
export function CheckboxGroup<Value extends PrimitiveValueType, Extra extends OptionExtra | undefined = undefined, RenderItem extends CheckboxGroupRenderItem<Value, Extra> = typeof DefaultCheckboxGroupItem>(
    props: PrimativeFieldProps<Value[]> & {
        asChild?: false;
        options: CheckboxGroupOption<Value, Extra>[];
        orientation?: 'horizontal' | 'vertical';
        renderItem?: RenderItem;
        className?: string;
        children?: ReactNode;
    } & Omit<HTMLAttributes<HTMLDivElement>, PrimativeFormPropsOmit | 'children' | 'className'>,
): ReactElement;
export function CheckboxGroup<Value extends PrimitiveValueType, Extra extends OptionExtra | undefined = undefined, RenderItem extends CheckboxGroupRenderItem<Value, Extra> = typeof DefaultCheckboxGroupItem>(props: CheckboxGroupProps<Value, Extra, RenderItem>) {
    const {
        asChild = false,
        name,
        value,
        onChange,
        onBlur,
        options,
        className,
        disabled,
        orientation = 'vertical',
        renderItem: RenderItem = DefaultCheckboxGroupItem,
        children,
        ...restProps
    } = props;

    const Component = asChild ? Slot : 'div';

    const [selectedValues, setSelectedValues] = useState<Value[]>([]);

    const { valueToString } = useMemo(() => createValueMapsFromOptions(options), [options]);

    const contextValue = useMemo<OptionFieldContextState<CheckboxGroupOption<Value, Extra>>>(() => ({
        options,
    }), [options]);

    useEffect(() => {
        setSelectedValues(value || []);
    }, [value]);

    const handleCheckedChange = useCallback((checked: boolean, option: Option<Value, Extra>) => {
        const newValue = checked
            ? [...selectedValues, option.value]
            : selectedValues.filter(v => v !== option.value);

        onChange(newValue);
    }, [selectedValues, onChange]);

    const isOptionChecked = useCallback((option: Option<Value, Extra>) => {
        return selectedValues.includes(option.value);
    }, [selectedValues]);

    const checkboxGroupContextValue = useMemo<CheckboxGroupContextState<PrimitiveValueType, OptionExtra | undefined>>(() => ({
        name,
        value: value as PrimitiveValueType[] | undefined,
        onChange: onChange as (value: PrimitiveValueType[]) => void,
        onBlur,
        disabled,
        selectedValues: selectedValues as PrimitiveValueType[],
        handleCheckedChange: handleCheckedChange as (checked: boolean, option: Option<PrimitiveValueType, OptionExtra | undefined>) => void,
        isOptionChecked: isOptionChecked as (option: Option<PrimitiveValueType, OptionExtra | undefined>) => boolean,
        valueToString: valueToString as Map<PrimitiveValueType, string>,
        RenderItem: RenderItem as (props: CheckboxGroupItemProps<PrimitiveValueType, OptionExtra | undefined>) => ReactNode,
    }), [name, value, onChange, onBlur, disabled, selectedValues, handleCheckedChange, isOptionChecked, valueToString, RenderItem]);

    const defaultChildren = useMemo(() => {
        if (children !== undefined) {
            return children;
        }
        return <CheckboxGroupItems <Value, Extra> />;
    }, [children]);

    return (
        <OptionFieldContext value={contextValue}>
            <CheckboxGroupContext value={checkboxGroupContextValue}>
                <Component
                    className={cn(
                        'flex gap-2',
                        orientation === 'horizontal' ? 'flex-row' : 'flex-col',
                        className,
                    )}
                    {...restProps}
                    children={defaultChildren}
                />
            </CheckboxGroupContext>
        </OptionFieldContext>
    );
}

CheckboxGroup.displayName = 'CheckboxGroup';
CheckboxGroup.Item = DefaultCheckboxGroupItem;
CheckboxGroup.Items = CheckboxGroupItems;

export type CheckboxGroupTriggerProps = Omit<React.ComponentPropsWithoutRef<typeof Button>, 'children'> & {
    placeholder?: string;
};

export function CheckboxGroupTrigger<Value extends PrimitiveValueType = PrimitiveValueType>(props: CheckboxGroupTriggerProps) {
    const { placeholder = 'Select options', ...restProps } = props;
    const { options } = useOptionField<CheckboxGroupOption<Value>>();
    const { value, onChange, name } = useCheckboxGroupContext<Value>();

    const selectedOptions = useMemo(() =>
        options.filter(option => (value || [])?.includes(option.value)), [value, options]);

    const handleRemove = (optionValue: Value) => {
        if (onChange) {
            onChange((value || []).filter(v => v !== optionValue));
        }
    };

    return (
        <Button
            variant="input"
            className="justify-start"
            {...restProps}
        >
            <div className="w-full flex items-center gap-2 overflow-hidden">
                {selectedOptions.length > 0
                    ? selectedOptions.map((option) => {
                            const stringValue = valueToString(option.value);
                            const id = name ? `${name}-${stringValue}` : stringValue;
                            return (
                                <Badge
                                    key={id}
                                    variant="outline"
                                    className="flex items-center gap-2 text-xs font-normal text-muted-foreground"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        handleRemove(option.value);
                                    }}
                                    aria-label={`Remove ${option.label}`}
                                    aria-describedby={id}
                                    aria-checked={true}
                                    role="checkbox"
                                >
                                    {option.label}
                                    <X size={12} />
                                </Badge>
                            );
                        })
                    : <span className="text-sm text-muted-foreground">{placeholder}</span>}
            </div>
            {selectedOptions.length > 0 && (
                <span className="text-sm">
                    {selectedOptions.length}
                    {' '}
                    selected
                </span>
            )}
            <ChevronDown />
        </Button>
    );
}

CheckboxGroupTrigger.displayName = 'CheckboxGroupTrigger';
CheckboxGroup.Trigger = CheckboxGroupTrigger;
